/**
 * La pantalla del cliente: what the till tells a second window about the sale.
 *
 * A laptop at the counter with a second monitor facing the customer opens this app a second
 * time, at `/pantalla-cliente`, and the till keeps it told what is being rung up. The two
 * windows talk over a `BroadcastChannel`: same browser, same computer, nothing on the network.
 * So the customer's screen keeps working with no connection and no server, which is the only
 * way anything at this counter can be allowed to work.
 *
 * The till sends what the customer should see and nothing else: names, how many, prices and
 * the total. No cost, no margin, no client balance; the screen faces whoever is at the counter
 * and whoever is behind them.
 */

import { useEffect, useRef } from 'react';
import type { Centavos, Milli } from '@abastece/shared';
import type { ProductoMiniatura } from '../ui/Miniatura.js';

export const RUTA_PANTALLA = '/pantalla-cliente';

/** Read by `main.tsx` before anything mounts: this window is the customer's, not a till. */
export const esPantallaCliente = () => location.pathname === RUTA_PANTALLA;

const CANAL = 'abastece.pantalla-cliente';

/** The window's name, so a second press brings the open one forward instead of a copy. */
const VENTANA = 'abastece-pantalla-cliente';

export interface LineaPantalla {
  clave: string;
  /** In the till's language, as the till shows it. */
  nombre: string;
  /**
   * What the picture needs: the photo's hash, read from this computer's own copy, and the
   * category and names that pick the drawing when there is no photo. `null` for a venta rápida.
   */
  miniatura: ProductoMiniatura | null;
  cantidad: Milli;
  /** The presentation's name, translated; `null` for a venta rápida. */
  unidad: string | null;
  precio: Centavos;
  subtotal: Centavos;
  /** At an offer (2026-11-03): the list's price without it, struck beside «Oferta». */
  precioRegular?: Centavos;
  /** At so many for fewer: its name, «2×1», and how many of the line are free. */
  lleva?: { nombre: string; gratis: Milli };
}

export type EstadoPantalla =
  /** No sale under way, or no till at all: the shop's name and a welcome. */
  | { tipo: 'espera' }
  /**
   * `total` with the delivery in it, when there is one, said on its own over it (`envio`); while it is
   * charged in cash, with the cash rounding in it too, said the same way (`redondeo`).
   */
  | {
      tipo: 'venta';
      lineas: LineaPantalla[];
      envio?: Centavos;
      redondeo?: Centavos;
      total: Centavos;
      cobrando: boolean;
      /** What the offers on it save the customer, when they save anything. */
      ahorro?: Centavos;
    }
  /** The sale just charged, with the change the customer should be handed, and what the offers saved. */
  | { tipo: 'gracias'; total: Centavos; cambio: Centavos; ahorro?: Centavos };

export type MensajePantalla =
  | { tipo: 'estado'; negocio: string; estado: EstadoPantalla }
  /** A screen that just opened, asking the till for what to show. */
  | { tipo: 'hola' };

type MensajeEstado = Extract<MensajePantalla, { tipo: 'estado' }>;

export const hayCanal = () => typeof BroadcastChannel !== 'undefined';

export function abrirCanal(): BroadcastChannel {
  return new BroadcastChannel(CANAL);
}

/**
 * Opens the customer's window, or brings it forward if it is already open.
 *
 * A popup, so it has no tabs or address bar to drag onto the customer's monitor. Whoever opens
 * it moves it there once and presses «Pantalla completa» inside it; a browser only lets a page
 * go full screen on a press in that page.
 */
export function abrirPantallaCliente(): void {
  const ventana = window.open('', VENTANA, 'popup,width=1024,height=640');
  if (!ventana) return;
  // Already showing the screen: leave it where it is, sale and all.
  if (ventana.location.pathname !== RUTA_PANTALLA) ventana.location.href = RUTA_PANTALLA;
  ventana.focus();
}

/**
 * The till's side: tells any open customer screen what to show, every time it changes.
 *
 * Also answers a screen that opens halfway through a sale, and says «espera» on the way out —
 * leaving the sale screen, or closing the till's window — so the customer is not left looking
 * at a sale nobody is ringing up any more.
 */
export function usePublicarPantalla(negocio: string, estado: EstadoPantalla): void {
  const mensaje: MensajeEstado = { tipo: 'estado', negocio, estado };
  const ultimo = useRef(mensaje);
  const canal = useRef<BroadcastChannel | null>(null);

  // Opened in the effect, not in render: StrictMode runs effects twice in development, and a
  // channel made once and closed by the first cleanup would throw on every message after it.
  useEffect(() => {
    if (!hayCanal()) return;
    const c = abrirCanal();
    canal.current = c;
    c.onmessage = (e: MessageEvent<MensajePantalla>) => {
      if (e.data?.tipo === 'hola') c.postMessage(ultimo.current);
    };
    c.postMessage(ultimo.current);
    const despedirse = () => c.postMessage({ ...ultimo.current, estado: { tipo: 'espera' } });
    window.addEventListener('pagehide', despedirse);
    return () => {
      window.removeEventListener('pagehide', despedirse);
      despedirse();
      c.close();
      canal.current = null;
    };
  }, []);

  // Compared as text: the till builds a fresh object every render, and only a real change
  // should reach the screen.
  const texto = JSON.stringify(mensaje);
  useEffect(() => {
    const m = JSON.parse(texto) as MensajeEstado;
    ultimo.current = m;
    canal.current?.postMessage(m);
  }, [texto]);
}
